import Image from 'next/image';
import { Variant } from 'shared/types/product';
import { ProductCardProduct } from 'shared/types/product/Product';
import Link from 'components/commercetools-ui/atoms/link';
import QuantityInput from 'components/commercetools-ui/organisms/quantity-input';
import { Button } from 'components/ui/button';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { RouteHelpers } from 'helpers/routerHelpers';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { cn } from 'helpers/utils/classnames';

interface CrossSellModalProductItemProps {
  product: ProductCardProduct;
  quantity: number;
  setQuantity: (quantity: number) => void;
  decreaseQuantity: () => void;
  increaseQuantity: () => void;
  handleAddToCart: (variant: Variant, quantity: number) => Promise<void>;
  isLoading: boolean;
}

function CrossSellModalProductItem({
  product,
  quantity,
  setQuantity,
  decreaseQuantity,
  increaseQuantity,
  handleAddToCart,
  isLoading,
}: CrossSellModalProductItemProps) {
  const image = product.variants[0]?.images?.[0];
  const variant = product?.variants[0];
  const price = variant?.price ? CurrencyHelpers.formatForCurrency(variant?.price) : null;
  const discountedPrice = variant?.discountedPrice ? CurrencyHelpers.formatForCurrency(variant?.discountedPrice) : null;
  const isClearance = variant?.originalPrice && variant.price?.centAmount !== variant.originalPrice.centAmount;
  const isDiscontinued = variant?.attributes?.isDiscontinued ?? false;
  const productDescription = generateProductDescription();

  function generateProductDescription() {
    let description = product.name;

    if (variant.attributes?.actualSize) {
      description += `, size ${variant.attributes.actualSize}`;
    }

    if (variant.attributes?.bulbBase) {
      description += `, bulb base ${variant.attributes.bulbBase}`;
    }

    return description;
  }

  const queryParams = RouteHelpers.encodeToURL(
    !variant?.attributes?.size
      ? {}
      : {
          size: variant?.attributes?.size,
        },
  );

  return (
    <li>
      <section className="size-full bg-[#fafaf6]">
        {image && (
          <Link
            link={`${product._url!}${queryParams}`}
            aria-label={`Go to ${product.name} product page`}
            className="relative block w-full pt-[100%]"
          >
            <Image
              fill
              loader={amplienceImageLoader}
              aria-label={`Image of the product ${productDescription}`}
              alt={`A ${productDescription}`}
              src={image}
              loading="lazy"
              sizes="(max-width: 1024px) 50vw, 25vw"
            />
          </Link>
        )}
        <section className="px-3 py-4 md:px-5">
          <Link link={`${product._url!}${queryParams}`} aria-label={`Go to ${product.name} product page`}>
            <h4 className="font-base line-clamp-2 min-h-[40px] text-center font-semibold leading-5">{product.name}</h4>
          </Link>

          {variant?.attributes?.actualSize && product.productType?.key === 'rugsAndRugAccessories' && (
            <p className="mt-3 text-center font-semibold">Size: {variant?.attributes.actualSize}</p>
          )}
          <div className="mt-3 flex items-center justify-center gap-2 text-center font-semibold">
            {discountedPrice && (
              <strong
                aria-label={`Discounted price: ${discountedPrice}`}
                className="block text-base leading-5 text-red-500"
              >
                {discountedPrice}
              </strong>
            )}
            {price && (
              <p
                className={cn('leading-[17px]', {
                  'line-through': discountedPrice,
                })}
              >
                <span className="sr-only">Original Price: </span>
                {price}
              </p>
            )}
          </div>

          <section className="mt-6 flex flex-col items-center gap-x-[10px] gap-y-4 md:flex-row">
            <QuantityInput
              maxQuantity={isClearance || isDiscontinued ? variant?.availableQuantity || 1 : 999}
              onChange={setQuantity}
              onDecreaseQuantity={decreaseQuantity}
              onIncreaseQuantity={increaseQuantity}
              quantity={quantity}
            />

            <Button
              disabled={isLoading}
              className={
                'disabled:opacity-30] mb-0 w-full flex-1 cursor-pointer rounded-[6px] bg-[#404040] py-3 text-sm font-semibold text-white hover:bg-[#333] disabled:cursor-not-allowed'
              }
              onClick={() => handleAddToCart(variant, quantity)}
              id="addToCartBtn"
              data-testid="addToCartBtn"
            >
              Add <span className="sr-only">${product.name} to cart</span>
            </Button>
          </section>
        </section>
      </section>
    </li>
  );
}

export default CrossSellModalProductItem;
